import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { productsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { convertToBase64 } from '../utils/formatters';

const CATEGORIES = [
  'Apparel', 'Electronics', 'Drinkware', 'Stationery', 'Bags & Travel',
  'Food & Gourmet', 'Wellness', 'Desk Accessories', 'Festive & Seasonal',
  'Luxury', 'Eco-Friendly', 'Custom/Other',
];

const STOCK_STATUS = ['In Stock', 'Available on Order', 'Limited Stock', 'Discontinued'];

export const ProductFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Electronics',
    description: '',
    cost_price: '',
    recommended_selling_price: '',
    moq: '',
    delivery_timeline: '',
    colors_variants: '',
    sourcing_origin: '',
    stock_status: 'In Stock',
    internal_notes: '',
    tags: '',
  });
  
  const [images, setImages] = useState([]);
  const [branding, setBranding] = useState({
    logo_print: false,
    embroidery: false,
    engraving: false,
    full_color_print: false,
    debossing: false,
    packaging_customization: false,
  });

  useEffect(() => {
    if (isEdit) {
      loadProduct();
    }
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await productsAPI.getOne(id);
      const product = response.data;
      setFormData({
        name: product.name,
        category: product.category,
        description: product.description,
        cost_price: product.cost_price || '',
        recommended_selling_price: product.recommended_selling_price,
        moq: product.moq,
        delivery_timeline: product.delivery_timeline,
        colors_variants: product.colors_variants,
        sourcing_origin: product.sourcing_origin || '',
        stock_status: product.stock_status,
        internal_notes: product.internal_notes || '',
        tags: product.tags.join(', '),
      });
      setImages(product.images || []);
      setBranding(product.branding_options);
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/catalogue');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      const newImages = await Promise.all(
        files.map(async (file) => {
          const base64 = await convertToBase64(file);
          return {
            data: base64,
            is_primary: images.length === 0,
          };
        })
      );
      setImages([...images, ...newImages]);
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload images');
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    if (newImages.length > 0 && images[index].is_primary) {
      newImages[0].is_primary = true;
    }
    setImages(newImages);
  };

  const setPrimaryImage = (index) => {
    const newImages = images.map((img, i) => ({
      ...img,
      is_primary: i === index,
    }));
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (images.length === 0) {
      toast.error('Please upload at least one product image');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        cost_price: parseFloat(formData.cost_price) || 0,
        recommended_selling_price: parseFloat(formData.recommended_selling_price),
        moq: parseInt(formData.moq),
        tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
        images,
        branding_options: branding,
      };

      if (isEdit) {
        await productsAPI.update(id, payload);
        toast.success('Product updated successfully');
      } else {
        await productsAPI.create(payload);
        toast.success('Product created successfully');
      }
      // Navigate to catalogue and trigger refresh
      navigate('/catalogue', { replace: false, state: { refresh: true } });
    } catch (error) {
      toast.error(isEdit ? 'Failed to update product' : 'Failed to create product');
      console.error('Product save error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/catalogue')}
          className="mb-6 gap-2"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Catalogue
        </Button>

        <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-8" data-testid="form-title">
          {isEdit ? 'Edit Product' : 'Add New Product'}
        </h1>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-8 space-y-6">
          <div>
            <Label htmlFor="name">Product Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Premium Leather Notebook"
              required
              className="mt-2"
              data-testid="product-name-input"
            />
          </div>

          <div>
            <Label>Product Images * (First image will be primary)</Label>
            <div className="mt-2">
              <label
                htmlFor="image-upload"
                className="flex items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-consortium-green transition-colors"
                data-testid="image-upload-area"
              >
                <div className="text-center">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Click to upload images</p>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 5MB</p>
                </div>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                {images.map((img, idx) => (
                  <div key={idx} className="relative group" data-testid={`uploaded-image-${idx}`}>
                    <img
                      src={img.data}
                      alt={`Product ${idx + 1}`}
                      className={`w-full aspect-square object-cover rounded-lg border-2 ${
                        img.is_primary ? 'border-consortium-green' : 'border-slate-200'
                      }`}
                      onClick={() => setPrimaryImage(idx)}
                    />
                    {img.is_primary && (
                      <span className="absolute top-1 left-1 bg-consortium-green text-white text-xs px-2 py-0.5 rounded">
                        Primary
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger className="mt-2" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="stock_status">Stock Status *</Label>
              <Select value={formData.stock_status} onValueChange={(value) => setFormData({ ...formData, stock_status: value })}>
                <SelectTrigger className="mt-2" data-testid="stock-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STOCK_STATUS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detailed product description, features, materials..."
              required
              rows={4}
              className="mt-2"
              data-testid="description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cost_price">Cost Price (₹) *</Label>
              <Input
                id="cost_price"
                type="number"
                step="0.01"
                value={formData.cost_price}
                onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                placeholder="0"
                required
                className="mt-2"
                data-testid="cost-price-input"
              />
              <p className="text-xs text-slate-500 mt-1">Visible to Admin only</p>
            </div>

            <div>
              <Label htmlFor="recommended_selling_price">Recommended Selling Price (₹) *</Label>
              <Input
                id="recommended_selling_price"
                type="number"
                step="0.01"
                value={formData.recommended_selling_price}
                onChange={(e) => setFormData({ ...formData, recommended_selling_price: e.target.value })}
                placeholder="0"
                required
                className="mt-2"
                data-testid="selling-price-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="moq">Minimum Order Quantity *</Label>
              <Input
                id="moq"
                type="number"
                value={formData.moq}
                onChange={(e) => setFormData({ ...formData, moq: e.target.value })}
                placeholder="100"
                required
                className="mt-2"
                data-testid="moq-input"
              />
            </div>

            <div>
              <Label htmlFor="delivery_timeline">Delivery Timeline *</Label>
              <Input
                id="delivery_timeline"
                value={formData.delivery_timeline}
                onChange={(e) => setFormData({ ...formData, delivery_timeline: e.target.value })}
                placeholder="10-14 working days"
                required
                className="mt-2"
                data-testid="delivery-timeline-input"
              />
            </div>
          </div>

          <div>
            <Label>Branding Possibilities</Label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {Object.entries({
                logo_print: 'Logo Print',
                embroidery: 'Embroidery',
                engraving: 'Engraving',
                full_color_print: 'Full Color Print',
                debossing: 'Debossing',
                packaging_customization: 'Packaging Customization',
              }).map(([key, label]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={branding[key]}
                    onCheckedChange={(checked) => setBranding({ ...branding, [key]: checked })}
                    data-testid={`branding-${key}`}
                  />
                  <Label htmlFor={key} className="cursor-pointer">{label}</Label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="colors_variants">Available Colors / Variants *</Label>
            <Input
              id="colors_variants"
              value={formData.colors_variants}
              onChange={(e) => setFormData({ ...formData, colors_variants: e.target.value })}
              placeholder="Black, Navy Blue, Grey"
              required
              className="mt-2"
              data-testid="colors-variants-input"
            />
          </div>

          <div>
            <Label htmlFor="sourcing_origin">Sourcing Origin</Label>
            <Input
              id="sourcing_origin"
              value={formData.sourcing_origin}
              onChange={(e) => setFormData({ ...formData, sourcing_origin: e.target.value })}
              placeholder="Domestic / Imported - China"
              className="mt-2"
              data-testid="sourcing-origin-input"
            />
            <p className="text-xs text-slate-500 mt-1">Visible to Admin only</p>
          </div>

          <div>
            <Label htmlFor="tags">Tags (comma-separated)</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="premium, eco-friendly, festive"
              className="mt-2"
              data-testid="tags-input"
            />
          </div>

          <div>
            <Label htmlFor="internal_notes">Internal Notes</Label>
            <Textarea
              id="internal_notes"
              value={formData.internal_notes}
              onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
              placeholder="Sourcing remarks, supplier details..."
              rows={3}
              className="mt-2"
              data-testid="internal-notes-input"
            />
            <p className="text-xs text-slate-500 mt-1">Visible to Admin only</p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="bg-consortium-green hover:bg-consortium-green-hover flex-1"
              data-testid="submit-product-btn"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/catalogue')}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
